import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import "./style.css";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import { CustomerCategory, ProductAvailability } from "../../utils/constants";
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import { SouthAfricanRand } from "../../utils/helpers";
import { round, split, trim } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../../constants";
import useSystemHook from "./../../hooks/SystemHook";

const GridProductsItem = ({
  title,
  price,
  invoicePrice,
  promo,
  promoInvoicePrice,
  customerCategory,
  vendor,
  image,
  productId,
  vendorImage,
  quality,
  fitment,
  availability,
  warranty,
  warrantyText,
  applicationPosition,
  applicationPositionImage,
  vendorDescription,
  showVehicleSpecificInformation,
  isKiosk,
  bodyHtml,
  sku,
  quantityInCart,
}) => {
  const { handleAddToCart } = useArgusCart();
  const [updatedQuantity, setUpdatedQuantity] = useState(quantityInCart);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const { isMobile, isTablet, isDesktop } = useSystemHook();

  const fitmentImage =
    "https://argusweb.azureedge.net/argusblob01/web/fitment.jpg";
  const qualityImage =
    "https://argusweb.azureedge.net/argusblob01/web/quality.jpg";
  const availabilityImage =
    "https://argusweb.azureedge.net/argusblob01/web/availability.jpg";

  const applicationPositionParts = split(applicationPosition, ":");
  if (applicationPositionParts && applicationPositionParts.length > 1) {
    applicationPosition = trim(applicationPositionParts[1]);
  }

  let fitmentText = split(fitment, "$");
  let fitmentTextSplit = [];
  if (fitmentText) {
    if (fitmentText.length > 1) {
      fitmentTextSplit = split(fitmentText[1], "|");
    } else {
      fitmentTextSplit = fitmentText;
    }
  }

  const colourForAvailability = (() => {
    switch (availability) {
      case ProductAvailability.InStock:
        return "success";
      case ProductAvailability.LowStock:
        return "warning";
      case ProductAvailability.ThreeToFiveDays:
        return "info";
      case ProductAvailability.No_Stock:
        return "error";
      default:
        return "default";
    }
  })();

  const colourForQuality = (() => {
    switch (quality) {
      case "A+":
        return "primary";
      case "A":
        return "info";
      case "B":
        return "secondary";
      case "Other":
        return "default";
      default:
        return "default";
    }
  })();

  const handleAddToCartClick = () => {
    handleAddToCart({
      id: productId,
      name: title,
      vendor,
      image,
      sku,
    });
  };

  const handleOpenModal = (content) => {
    setModalContent(content);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalContent(null);
  };

  const sanitizedData = () => ({
    __html: DOMPurify.sanitize(bodyHtml),
  });

  useEffect(() => {
    setUpdatedQuantity(Number(quantityInCart));
  }, [quantityInCart]);

  return (
    <Card
      raised
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "0.125rem",
        boxShadow: "none",
        border: `0.0625rem solid ${COLORS.border}`,
        overflow: "hidden",
        position: "relative",
        minHeight: "20rem",
      }}
    >
      {/* Sale Tag */}
      {/* <Box
        sx={{
          position: "absolute",
          top: "0.5rem",
          left: "0.5rem",
          backgroundColor: COLORS.themeYellow,
          color: COLORS.darkText,
          padding: "0.25rem 0.5rem",
          borderRadius: "0.25rem",
          zIndex: 2,
          textTransform: "uppercase",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
          Sale
        </Typography>
      </Box> */}

      {/* Product Image */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "12.5rem",
          width: "100%",
          padding: "0.5rem",
        }}
      >
        <a
          href={`/product/${encodeURIComponent(sku)}`}
          style={{ display: "block", width: "100%", height: "100%" }}
        >
          <CardMedia
            component="img"
            image={image}
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </a>
      </Box>

      {/* Product Details */}
      <Box
        sx={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          padding: "0.75rem",
          justifyContent: "space-between",
        }}
      >
        {/* Product Type */}
        <Typography
          variant="caption"
          color={COLORS.lightText}
          sx={{
            fontWeight: 200,
            fontSize: isMobile ? "var(--font-size-xxs)" : "var(--font-size-xs)",
            mb: 0.5,
          }}
        >
          {split(title, " - ")[0] || "Product Type"}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          color={COLORS.darkText}
          sx={{
            fontWeight: 500,
            fontSize: isMobile ? "var(--font-size-xxs)" : "var(--font-size-s)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.2em",
          }}
        >
          {title}
        </Typography>

        {/* Availability, Warranty, Quality, Info Row */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ gap: "0.25rem", flexWrap: "wrap", marginTop: "0.5rem" }}
        >
          {availability && (
            <Chip
              size="small"
              label={availability}
              icon={
                availability === ProductAvailability.InStock ? (
                  <CheckCircleIcon
                    sx={{
                      fontSize: isMobile
                        ? "var(--font-size-xxs)"
                        : "var(--font-size-l)",
                      color: COLORS.themeGreen,
                    }}
                  />
                ) : availability === ProductAvailability.LowStock ? (
                  <WarningIcon
                    sx={{
                      fontSize: isMobile
                        ? "var(--font-size-xxs)"
                        : "var(--font-size-l)",
                      color: COLORS.themeOrange,
                    }}
                  />
                ) : availability === ProductAvailability.No_Stock ? (
                  <DoNotDisturbOnIcon
                    sx={{
                      fontSize: isMobile
                        ? "var(--font-size-xxs)"
                        : "var(--font-size-l)",
                      color: COLORS.themeRed,
                    }}
                  />
                ) : null
              }
              onClick={() =>
                handleOpenModal(
                  <img
                    src={availabilityImage}
                    alt="Availability"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "37.5rem",
                      maxHeight: "25rem",
                    }}
                  />
                )
              }
              sx={{
                fontSize: isMobile ? "0.5rem" : "var(--font-size-xxs)",
                height: "1.25rem",
                backgroundColor:
                  availability === ProductAvailability.InStock
                    ? COLORS.themeGreen
                    : availability === ProductAvailability.LowStock
                    ? COLORS.themeOrange
                    : availability === ProductAvailability.No_Stock
                    ? COLORS.themeRed
                    : COLORS.border,
                color: COLORS.white,
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          )}
          {!!warranty && !!warrantyText && (
            <Tooltip title={warrantyText}>
              <Chip
                size="small"
                label={warranty}
                color="error"
                onClick={() => handleOpenModal(warrantyText)}
                sx={{ fontSize: "0.625rem", height: "1.25rem" }}
              />
            </Tooltip>
          )}
          {quality && (
            <Chip
              size="small"
              label={quality}
              color={colourForQuality || "default"}
              onClick={() =>
                handleOpenModal(
                  <img
                    src={qualityImage}
                    alt="Quality"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "37.5rem",
                      maxHeight: "25rem",
                    }}
                  />
                )
              }
              sx={{ fontSize: "0.625rem", height: "1.25rem" }}
            />
          )}
          {/* <IconButton
            onClick={() =>
              handleOpenModal(<div dangerouslySetInnerHTML={sanitizedData()} />)
            }
          >
            <InfoIcon sx={{ fontSize: "1.1rem", ml: -1 }} />
          </IconButton> */}
        </Box>

        {/* SKU */}
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            fontSize: isMobile ? "var(--font-size-xxs)" : "var(--font-size-xs)",
            marginTop: "0.25rem",
            color: COLORS.darkText,
            fontWeight: 300,
          }}
        >
          SKU: {sku}
        </Typography>

        {/* Price and Add to Cart Row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginTop: "auto" }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
          >
            {promo > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: "0.75rem",
                    textDecoration: "line-through",
                    fontWeight: 500,
                  }}
                >
                  {price > 0
                    ? SouthAfricanRand.format(round(price, 2))
                    : "Unavailable"}
                </Typography>
                {customerCategory === CustomerCategory.Commercial &&
                  price > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      (Ex. VAT)
                    </Typography>
                  )}
              </Box>
            )}
            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}
            >
              <Typography
                variant="body1"
                color={COLORS.themeRed}
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile
                    ? "var(--font-size-m)"
                    : "var(--font-size-l)",
                }}
              >
                {SouthAfricanRand.format(round(promo > 0 ? promo : price, 2))}
              </Typography>
              {customerCategory === CustomerCategory.Commercial &&
                promo === 0 && (
                  <Typography variant="caption" color="textSecondary">
                    (Ex. VAT)
                  </Typography>
                )}
            </Box>
          </Box>
          {availability !== ProductAvailability.No_Stock && (
            <IconButton
              onClick={handleAddToCartClick}
              sx={{
                padding: "0.25rem",
                border: `0.0625rem solid ${COLORS.border}`,
                borderRadius: "2px",
              }}
            >
              <ShoppingCartIcon
                sx={{ color: COLORS.lightText, fontSize: "1.25rem" }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Modal for Additional Info */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "25rem" },
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: "0.5rem",
            boxShadow: 24,
            p: "1.5rem",
            overflowY: "auto",
          }}
        >
          <IconButton
            sx={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </IconButton>
          {modalContent}
        </Box>
      </Modal>
    </Card>
  );
};

export default GridProductsItem;
